import { useState } from 'react';
import { 
  Phone, Mail, MapPin, Clock, Send, Truck,
  CheckCircle, Award
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const Contact = () => {
  const [quoteForm, setQuoteForm] = useState({
    fullName: '',
    company: '',
    phone: '',
    email: '',
    pickupLocation: '',
    deliveryLocation: '',
    freightType: '',
    weight: '',
    notes: '',
  });

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleQuoteChange = (e) => {
    setQuoteForm({ ...quoteForm, [e.target.name]: e.target.value });
  };

  const handleContactChange = (e) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });
  };

  const handleQuoteSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Quote Request - ${quoteForm.company || quoteForm.fullName}`);
    const body = encodeURIComponent(
      `Quote Request Details:\n\n` +
      `Name: ${quoteForm.fullName}\n` +
      `Company: ${quoteForm.company}\n` +
      `Phone: ${quoteForm.phone}\n` +
      `Email: ${quoteForm.email}\n\n` +
      `Shipment Details:\n` +
      `Pickup: ${quoteForm.pickupLocation}\n` +
      `Delivery: ${quoteForm.deliveryLocation}\n` +
      `Freight Type: ${quoteForm.freightType}\n` +
      `Weight: ${quoteForm.weight}\n\n` +
      `Additional Notes:\n${quoteForm.notes}`
    );
    window.location.href = `mailto:dispatch.douala@gmail.com?subject=${subject}&body=${body}`;
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(contactForm.subject || 'Contact Form Inquiry');
    const body = encodeURIComponent(
      `Contact Form Message:\n\n` +
      `From: ${contactForm.name}\n` +
      `Email: ${contactForm.email}\n\n` +
      `Message:\n${contactForm.message}`
    );
    window.location.href = `mailto:dispatch.douala@gmail.com?subject=${subject}&body=${body}`;
  };

  const freightTypes = [
    'Dry Van',
    'Reefer (Temperature Controlled)',
    'Flatbed',
    'Power-Only',
    'Box Truck',
    'Expedited',
    'Team Driving',
  ];

  return (
    <div className="bg-white" data-testid="contact-page">
      {/* Hero Header */}
      <section className="bg-[#0B2A5B] py-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#FFD700] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
            Get In Touch
          </span>
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight font-['Montserrat'] mb-4">
            Contact & Quote Request
          </h1>
          <p className="text-white/70 max-w-2xl mx-auto">
            Ready to ship? Get a quote or contact our dispatch team for immediate assistance.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding" data-testid="contact-content">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-2">
              <span className="text-[#C62828] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
                Contact Information
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0B2A5B] tracking-tight mb-6 font-['Montserrat']">
                Let's Talk Freight
              </h2>

              <div className="space-y-6 mb-8">
                <a
                  href="tel:773-344-7748"
                  className="flex items-start space-x-4 group"
                  data-testid="contact-phone-link"
                >
                  <div className="bg-[#0B2A5B] p-3 rounded-sm group-hover:bg-[#C62828] transition-colors">
                    <Phone className="h-5 w-5 text-[#FFD700]" />
                  </div>
                  <div>
                    <p className="text-[#0B2A5B] font-bold font-['Montserrat']">Phone</p>
                    <p className="text-[#6B7280]">773-344-7748</p>
                  </div>
                </a>

                <a
                  href="mailto:dispatch.douala@gmail.com"
                  className="flex items-start space-x-4 group"
                  data-testid="contact-email-link"
                >
                  <div className="bg-[#0B2A5B] p-3 rounded-sm group-hover:bg-[#C62828] transition-colors">
                    <Mail className="h-5 w-5 text-[#FFD700]" />
                  </div>
                  <div>
                    <p className="text-[#0B2A5B] font-bold font-['Montserrat']">Email</p>
                    <p className="text-[#6B7280]">dispatch.douala@gmail.com</p>
                  </div>
                </a>

                <div className="flex items-start space-x-4">
                  <div className="bg-[#0B2A5B] p-3 rounded-sm">
                    <MapPin className="h-5 w-5 text-[#FFD700]" />
                  </div>
                  <div>
                    <p className="text-[#0B2A5B] font-bold font-['Montserrat']">Service Area</p>
                    <p className="text-[#6B7280]">All 48 Continental United States</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-[#0B2A5B] p-3 rounded-sm">
                    <Clock className="h-5 w-5 text-[#FFD700]" />
                  </div>
                  <div>
                    <p className="text-[#0B2A5B] font-bold font-['Montserrat']">Dispatch Availability</p>
                    <p className="text-[#6B7280]">24/7 - Always Available</p>
                  </div>
                </div>
              </div>

              {/* Compliance Box */}
              <div className="bg-[#0B2A5B] p-6 rounded-sm">
                <div className="flex items-center space-x-2 mb-4">
                  <Award className="h-5 w-5 text-[#FFD700]" />
                  <span className="text-white font-bold text-sm uppercase tracking-wider font-['Montserrat']">
                    Federal Authority
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider">USDOT</p>
                    <p className="text-white font-bold">2520499</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider">MC</p>
                    <p className="text-white font-bold">874923</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-[#FFD700]" />
                  <span className="text-white/80 text-sm">Fully Licensed & Insured</span>
                </div>
              </div>
            </div>

            {/* Quote Form */}
            <div className="lg:col-span-3">
              <div className="bg-[#F8FAFC] p-8 rounded-sm">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="bg-[#C62828] p-2 rounded-sm">
                    <Truck className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0B2A5B] font-['Montserrat']">
                    Request a Quote
                  </h3>
                </div>

                <form onSubmit={handleQuoteSubmit} className="space-y-6" data-testid="quote-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fullName" className="text-[#0B2A5B] font-semibold">
                        Full Name *
                      </Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        value={quoteForm.fullName}
                        onChange={handleQuoteChange}
                        required
                        className="mt-1.5 form-input"
                        placeholder="John Doe"
                        data-testid="quote-fullname"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-[#0B2A5B] font-semibold">
                        Company
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        value={quoteForm.company}
                        onChange={handleQuoteChange}
                        className="mt-1.5 form-input"
                        placeholder="Company Name"
                        data-testid="quote-company"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="text-[#0B2A5B] font-semibold">
                        Phone *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={quoteForm.phone}
                        onChange={handleQuoteChange}
                        required
                        className="mt-1.5 form-input"
                        placeholder="(555) 123-4567"
                        data-testid="quote-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#0B2A5B] font-semibold">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={quoteForm.email}
                        onChange={handleQuoteChange}
                        required
                        className="mt-1.5 form-input"
                        placeholder="email@company.com"
                        data-testid="quote-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pickupLocation" className="text-[#0B2A5B] font-semibold">
                        Pickup Location *
                      </Label>
                      <Input
                        id="pickupLocation"
                        name="pickupLocation"
                        value={quoteForm.pickupLocation}
                        onChange={handleQuoteChange}
                        required
                        className="mt-1.5 form-input"
                        placeholder="City, State"
                        data-testid="quote-pickup"
                      />
                    </div>
                    <div>
                      <Label htmlFor="deliveryLocation" className="text-[#0B2A5B] font-semibold">
                        Delivery Location *
                      </Label>
                      <Input
                        id="deliveryLocation"
                        name="deliveryLocation"
                        value={quoteForm.deliveryLocation}
                        onChange={handleQuoteChange}
                        required
                        className="mt-1.5 form-input"
                        placeholder="City, State"
                        data-testid="quote-delivery"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="freightType" className="text-[#0B2A5B] font-semibold">
                        Freight Type *
                      </Label>
                      <Select
                        value={quoteForm.freightType}
                        onValueChange={(value) => setQuoteForm({ ...quoteForm, freightType: value })}
                      >
                        <SelectTrigger className="mt-1.5 form-input" data-testid="quote-freight-type">
                          <SelectValue placeholder="Select freight type" />
                        </SelectTrigger>
                        <SelectContent>
                          {freightTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="weight" className="text-[#0B2A5B] font-semibold">
                        Estimated Weight
                      </Label>
                      <Input
                        id="weight"
                        name="weight"
                        value={quoteForm.weight}
                        onChange={handleQuoteChange}
                        className="mt-1.5 form-input"
                        placeholder="e.g., 20,000 lbs"
                        data-testid="quote-weight"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes" className="text-[#0B2A5B] font-semibold">
                      Additional Notes
                    </Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={quoteForm.notes}
                      onChange={handleQuoteChange}
                      className="mt-1.5 form-input min-h-[100px]"
                      placeholder="Special requirements, timeline, etc."
                      data-testid="quote-notes"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full btn-primary flex items-center justify-center space-x-2"
                    data-testid="quote-submit-btn"
                  >
                    <Send className="h-4 w-4" />
                    <span>Send Quote Request</span>
                  </button>

                  <p className="text-center text-[#6B7280] text-sm">
                    Your request will be received via email. Dispatch will respond within 24 hours.
                  </p>
                </form>
              </div>

              {/* Contact Form */}
              <div className="mt-8 bg-white border border-slate-200 p-8 rounded-sm">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="bg-[#0B2A5B] p-2 rounded-sm">
                    <Mail className="h-5 w-5 text-[#FFD700]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0B2A5B] font-['Montserrat']">
                    General Inquiry
                  </h3>
                </div>

                <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contactName" className="text-[#0B2A5B] font-semibold">
                        Name *
                      </Label>
                      <Input
                        id="contactName"
                        name="name"
                        value={contactForm.name}
                        onChange={handleContactChange}
                        required
                        className="mt-1.5 form-input"
                        placeholder="Your name"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contactEmail" className="text-[#0B2A5B] font-semibold">
                        Email *
                      </Label>
                      <Input
                        id="contactEmail"
                        name="email"
                        type="email"
                        value={contactForm.email}
                        onChange={handleContactChange}
                        required
                        className="mt-1.5 form-input"
                        placeholder="email@example.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-[#0B2A5B] font-semibold">
                      Subject *
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={contactForm.subject}
                      onChange={handleContactChange}
                      required
                      className="mt-1.5 form-input"
                      placeholder="How can we help?"
                      data-testid="contact-subject"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#0B2A5B] font-semibold">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={contactForm.message}
                      onChange={handleContactChange}
                      required
                      className="mt-1.5 form-input min-h-[120px]"
                      placeholder="Your message..."
                      data-testid="contact-message"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-secondary flex items-center justify-center space-x-2 w-full"
                    data-testid="contact-submit-btn"
                  >
                    <Send className="h-4 w-4" />
                    <span>Send Message</span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
